import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  ShoppingCart as CartIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  LocalOffer as OfferIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    height: "100%",
  },
  statIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  statValue: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  categoryList: {
    padding: theme.spacing(2),
  },
  categoryItem: {
    marginBottom: theme.spacing(2),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  categoryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  categoryName: {
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  categoryValue: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  categoryBar: {
    height: 8,
    borderRadius: 4,
  },
  categoryPercentage: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  pieChartContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  pieChart: {
    position: "relative",
    width: 200,
    height: 200,
  },
  pieChartCenter: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    backgroundColor: "white",
    borderRadius: "50%",
    width: 80,
    height: 80,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  legendContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginLeft: theme.spacing(3),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  infoCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.light + "15",
    borderLeft: `4px solid ${theme.palette.info.main}`,
  },
}));

const CATEGORY_COLORS = [
  "#2196F3", // Blue
  "#4CAF50", // Green
  "#FF9800", // Orange
  "#9C27B0", // Purple
  "#F44336", // Red
  "#00BCD4", // Cyan
  "#795548", // Brown
  "#607D8B", // Blue Grey
  "#E91E63", // Pink
  "#3F51B5", // Indigo
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const formatDays = (days) => {
  if (days === null || days === undefined) return "-";
  if (days === 0) return "Hoje";
  if (days === 1) return "Ontem";
  if (days < 7) return `${days} dias`;
  if (days < 30) return `${Math.floor(days / 7)} semana${Math.floor(days / 7) > 1 ? "s" : ""}`;
  if (days < 365) return `${Math.floor(days / 30)} mês${Math.floor(days / 30) > 1 ? "es" : ""}`;
  return `${Math.floor(days / 365)} ano${Math.floor(days / 365) > 1 ? "s" : ""}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Simple SVG Pie Chart component
const PieChart = ({ data, size = 200 }) => {
  const classes = useStyles();

  if (!data || data.length === 0) {
    return null;
  }

  const total = data.reduce((sum, item) => sum + item.totalSpent, 0);
  let currentAngle = -90; // Start at top

  const segments = data.map((item, index) => {
    const percentage = (item.totalSpent / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    // Calculate arc path
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const radius = size / 2;
    const centerX = size / 2;
    const centerY = size / 2;

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    return (
      <path
        key={index}
        d={pathData}
        fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
        stroke="white"
        strokeWidth="2"
      />
    );
  });

  return (
    <Box className={classes.pieChartContainer}>
      <Box className={classes.pieChart}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments}
        </svg>
        <Box className={classes.pieChartCenter}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {data.length}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            categorias
          </Typography>
        </Box>
      </Box>
      <Box className={classes.legendContainer}>
        {data.slice(0, 5).map((item, index) => (
          <Box key={item.categoryId} className={classes.legendItem}>
            <Box
              className={classes.legendColor}
              style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
            />
            <Typography variant="caption">
              {item.categoryName} ({item.percentage.toFixed(0)}%)
            </Typography>
          </Box>
        ))}
        {data.length > 5 && (
          <Typography variant="caption" color="textSecondary">
            +{data.length - 5} outras
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const CustomerStatsTab = ({ stats, customer }) => {
  const classes = useStyles();

  if (!stats) {
    return (
      <Box className={classes.emptyState}>
        <TrendingIcon style={{ fontSize: 48, opacity: 0.5 }} />
        <Typography variant="body1">Estatisticas nao disponiveis</Typography>
        <Typography variant="body2" color="textSecondary">
          Este cliente ainda nao possui historico de compras
        </Typography>
      </Box>
    );
  }

  const hasOrders = stats.totalOrders > 0;

  return (
    <Box>
      {/* Main Stats Cards */}
      <Grid container spacing={2} className={classes.statsGrid}>
        <Grid item xs={6} sm={3}>
          <Paper className={classes.statCard} elevation={1}>
            <CartIcon className={classes.statIcon} color="primary" />
            <Typography className={classes.statValue}>{stats.totalOrders}</Typography>
            <Typography className={classes.statLabel}>Pedidos</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className={classes.statCard} elevation={1}>
            <MoneyIcon className={classes.statIcon} style={{ color: "#4CAF50" }} />
            <Typography className={classes.statValue} style={{ color: "#4CAF50" }}>
              {formatCurrency(stats.totalSpent)}
            </Typography>
            <Typography className={classes.statLabel}>Total Gasto</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className={classes.statCard} elevation={1}>
            <TrendingIcon className={classes.statIcon} style={{ color: "#FF9800" }} />
            <Typography className={classes.statValue} style={{ color: "#FF9800" }}>
              {formatCurrency(stats.averageTicket)}
            </Typography>
            <Typography className={classes.statLabel}>Ticket Medio</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className={classes.statCard} elevation={1}>
            <ScheduleIcon className={classes.statIcon} style={{ color: "#9C27B0" }} />
            <Typography className={classes.statValue} style={{ color: "#9C27B0", fontSize: "1.2rem" }}>
              {formatDays(stats.daysSinceLastOrder)}
            </Typography>
            <Typography className={classes.statLabel}>Último Pedido</Typography>
            {stats.lastOrderAt && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                {formatDateTime(stats.lastOrderAt)}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Additional Info */}
      {hasOrders && (
        <Paper className={classes.infoCard} elevation={0}>
          <Grid container spacing={2}>
            {stats.favoritePaymentMethod && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PaymentIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Forma de Pagamento Preferida
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {stats.favoritePaymentMethod}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {stats.topCategory && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CategoryIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Categoria Mais Comprada
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {stats.topCategory}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Category Breakdown */}
      {stats.purchasesByCategory && stats.purchasesByCategory.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            <CategoryIcon color="primary" />
            Compras por Categoria
          </Typography>

          {/* Pie Chart */}
          <Paper elevation={1}>
            <PieChart data={stats.purchasesByCategory} />
          </Paper>

          {/* Category List with Bars */}
          <Paper className={classes.categoryList} elevation={1} style={{ marginTop: 16 }}>
            {stats.purchasesByCategory.map((category, index) => (
              <Box key={category.categoryId} className={classes.categoryItem}>
                <Box className={classes.categoryHeader}>
                  <Box className={classes.categoryName}>
                    <Box
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                      }}
                    />
                    <Typography variant="body2">{category.categoryName}</Typography>
                  </Box>
                  <Typography variant="body2" className={classes.categoryValue}>
                    {formatCurrency(category.totalSpent)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={category.percentage}
                  className={classes.categoryBar}
                  style={{
                    backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] + "30",
                  }}
                />
                <Typography className={classes.categoryPercentage}>
                  {category.totalQuantity} itens ({category.percentage.toFixed(1)}% do total)
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      {/* Customer Profile Tags */}
      <Box className={classes.section}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          <OfferIcon color="primary" />
          Perfil do Cliente
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {stats.hasAddress ? (
            <Chip label="Endereco cadastrado" color="primary" variant="outlined" size="small" />
          ) : (
            <Chip label="Sem endereco" variant="outlined" size="small" />
          )}
          {stats.isIdentified ? (
            <Chip label="Identificado (CPF/CNPJ)" color="primary" variant="outlined" size="small" />
          ) : (
            <Chip label="Nao identificado" variant="outlined" size="small" />
          )}
          {stats.totalOrders >= 10 && (
            <Chip label="Cliente frequente" color="secondary" size="small" />
          )}
          {stats.totalSpent >= 1000 && (
            <Chip label="Alto valor" style={{ backgroundColor: "#FFD700", color: "#333" }} size="small" />
          )}
          {stats.daysSinceLastOrder > 60 && (
            <Chip label="Inativo ha 60+ dias" color="default" size="small" />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerStatsTab;
